import { Button, Form, Input, Modal, message } from 'antd';
import React, { FC, useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { nullSafeGet } from '../../utils/DataAccessUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { FieldTechHomeAddress } from '../../utils/WrenchModeEventUtils';
import {
	FieldTechHomeAddressPayload,
	createFieldTechHomeAddress,
	updateFieldTechHomeAddress,
} from '../../thunks/field_techs_thunks';
import { LocationAutocomplete } from '../location_autocomplete/LocationAutocomplete';
import { OWButton } from '../ow_button/OWButton';

const INVALID_ADDRESS_MESSAGE = 'Please search for and select a valid address';
const FORM_ID = 'field-tech-home-address-form';

interface EditFieldTechHomeAddressModalProps {
	fieldTechEmail: string;
	homeAddress: FieldTechHomeAddress | null;
	onCancel: () => void;
	onSuccess: () => void;
	createHomeAddress: (homeAddress: FieldTechHomeAddressPayload) => Promise<unknown>;
	updateHomeAddress: (homeAddress: FieldTechHomeAddressPayload) => Promise<unknown>;
}

const EditFieldTechHomeAddressModal: FC<EditFieldTechHomeAddressModalProps> = ({
	fieldTechEmail,
	homeAddress,
	onCancel,
	onSuccess,
	createHomeAddress,
	updateHomeAddress,
}): React.ReactElement => {
	const [form] = Form.useForm();
	const [saving, setSaving] = useState(false);
	const latitude = Form.useWatch('latitude', form);
	const longitude = Form.useWatch('longitude', form);
	const isUpdate = !!homeAddress;

	const handleAddressSelect = useCallback(
		(addressText, latLng, parsedAddress) => {
			form.setFieldsValue({
				address: parsedAddress,
				latitude: latLng.lat,
				longitude: latLng.lng,
			});
			form.validateFields(['address']);
		},
		[form]
	);

	// Edited text is stale until its geocode lands, so the validator blocks Save until then.
	const handleAddressInputChange = useCallback(
		() => form.setFieldsValue({ latitude: undefined, longitude: undefined }),
		[form]
	);

	// The backend needs a geocoded point (lat/lng) as well as the address lines it came from.
	const validateGeocodedAddress = useCallback(
		(_, address) => {
			const isGeocoded =
				!!nullSafeGet('streetAddress1', address) &&
				!!nullSafeGet('city', address) &&
				!!nullSafeGet('region', address) &&
				!!nullSafeGet('country', address) &&
				form.getFieldValue('latitude') != null &&
				form.getFieldValue('longitude') != null;
			return isGeocoded ? Promise.resolve() : Promise.reject(new Error(INVALID_ADDRESS_MESSAGE));
		},
		[form]
	);

	const handleSubmit = useCallback(
		(values) => {
			const payload: FieldTechHomeAddressPayload = {
				fieldTechEmail,
				name: values.name.trim(),
				address: values.address,
				latitude: values.latitude,
				longitude: values.longitude,
			};
			const request = isUpdate
				? updateHomeAddress({ ...payload, id: homeAddress.id })
				: createHomeAddress(payload);
			setSaving(true);
			request
				.then(() => onSuccess && onSuccess())
				.catch((error) =>
					message.error(typeof error === 'string' ? error : 'Unable to save home address.')
				)
				.finally(() => setSaving(false));
		},
		[createHomeAddress, fieldTechEmail, homeAddress, isUpdate, onSuccess, updateHomeAddress]
	);

	return (
		<Modal
			visible={true}
			width={600}
			title={isUpdate ? 'Edit Home Address' : 'Add Home Address'}
			onCancel={onCancel}
			closable={true}
			destroyOnClose={true}
			footer={[
				<Button key="cancel" onClick={onCancel}>
					Cancel
				</Button>,
				<OWButton key="submit" type="primary" htmlType="submit" form={FORM_ID} loading={saving}>
					Save
				</OWButton>,
			]}
		>
			<Form
				form={form}
				id={FORM_ID}
				layout="vertical"
				className="supplierForm"
				preserve={false}
				initialValues={{
					name: nullSafeGet('name', homeAddress),
					address: nullSafeGet('address', homeAddress),
					latitude: nullSafeGet('latitude', homeAddress),
					longitude: nullSafeGet('longitude', homeAddress),
				}}
				onFinish={handleSubmit}
			>
				<Form.Item hidden name="latitude" />
				<Form.Item hidden name="longitude" />
				<Form.Item
					name="name"
					label="Name"
					rules={[{ required: true, whitespace: true, message: 'Please give the address a name.' }]}
				>
					<Input placeholder="e.g. Home" />
				</Form.Item>
				<Form.Item
					name="address"
					label="Address"
					rules={[{ required: true, validator: validateGeocodedAddress }]}
				>
					<LocationAutocomplete
						placeholder="Search for and select an address"
						lat={latitude}
						lon={longitude}
						showCoordinateEditor={true}
						onSelect={handleAddressSelect}
						onInputChange={handleAddressInputChange}
					/>
				</Form.Item>
			</Form>
		</Modal>
	);
};

const mapDispatchToProps = (dispatch) => ({
	createHomeAddress: (homeAddress: FieldTechHomeAddressPayload) =>
		dispatch(createFieldTechHomeAddress(ROLE_TYPES.SUPPLIER)(homeAddress)),
	updateHomeAddress: (homeAddress: FieldTechHomeAddressPayload) =>
		dispatch(updateFieldTechHomeAddress(ROLE_TYPES.SUPPLIER)(homeAddress)),
});

export default connect(null, mapDispatchToProps)(EditFieldTechHomeAddressModal);
